import { hoursToSeconds } from 'date-fns';
import { format } from 'date-fns-tz';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { DeliverySlot } from '~/api/ETA';
import { company } from '~/company/Company';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '~/components/isolated/Carousel';
import Modal from '~/components/Modal/Modal';
import DateItem from '~/components/ModalSlotsSelectDate/DateItem';
import ExpressDateItem from '~/components/ModalSlotsSelectDate/ExpressDateItem';
import { useDateLang } from '~/hooks/useDateLang';
import { useGlobal } from '~/hooks/useGlobal';
import { useModal } from '~/hooks/useModal';
import { useTimeFormat } from '~/hooks/useTimeFormat';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import {
  cartStore,
  checkoutStore,
  mainStore,
  orderStore,
  userStore,
} from '~/stores';
import { formatPriceWithCurrency } from '~/utils/formaters';

import Icon from '../Icon/Icon';

import { useDeliverySlotsInfo } from './hooks';
import styles from './ModalSlotsSelectDate.module.scss';

const ModalSlotsSelectDate = () => {
  const { t } = useTranslation();
  const { isMobile } = useGlobal();
  const { closeModal } = useModal();
  const { locale } = useDateLang();
  const { timeFormat } = useTimeFormat();

  const { currentDelivery, deliverySlots, isExpressDeliveryAvailable, now } =
    useDeliverySlotsInfo();

  const [selectedSlotId, setSelectedSlotId] = useState<string | undefined>();
  const [selectedDate, setSelectedDate] = useState<string | undefined>();

  useEffect(() => {
    if (selectedSlotId || selectedDate) {
      return;
    }

    const setFirstSlot = () => {
      const slot = checkoutStore.sortedDeliverySlotsList?.at(0);

      if (!slot) {
        return;
      }

      setSelectedSlotId(slot.schedule_slot_id);
      setSelectedDate(`${slot.current_date * 1000}`);
    };

    if (!currentDelivery) {
      setFirstSlot();
      return;
    }

    if (!currentDelivery.slot) {
      return;
    }

    const existedSlot =
      checkoutStore.slotSelectionModalStore.getSlotByIdAndDate(
        currentDelivery.slot.schedule_slot_id,
        `${currentDelivery.slot.current_date}`,
      );

    if (!existedSlot) {
      setFirstSlot();
      return;
    }

    setSelectedSlotId(existedSlot.schedule_slot_id);
    setSelectedDate(`${existedSlot.current_date * 1000}`);
  }, []);

  const lastBackorder =
    checkoutStore.isDeliverTogether && !isExpressDeliveryAvailable
      ? cartStore.selectedCartItems.reduce((acc, { backorderLeadTime }) => {
          const backorderTime = now + hoursToSeconds(backorderLeadTime);

          return Math.max(acc, backorderTime);
        }, 0)
      : 0;

  const timeStamps =
    lastBackorder > 0
      ? Object.keys(deliverySlots).filter((i) => {
          return deliverySlots[i]?.some((j) => {
            return j.cut_off_time >= lastBackorder;
          });
        })
      : Object.keys(deliverySlots);

  const initPosition = selectedDate ? timeStamps.indexOf(selectedDate) : 0;

  const carouselOpts = useMemo(
    () => ({
      align: 'start' as const,
      slidesToScroll: 1,
      containScroll: 'trimSnaps' as const,
      startIndex: initPosition,
    }),
    [initPosition],
  );

  const handleSetExpressDelivery = () => {
    setSelectedDate(undefined);
    setSelectedSlotId(undefined);

    analyticsService.track(AnalyticsEvent.DELIVERY_METHOD_SELECTED, {
      type: 'Express',
    });
  };

  const Component = isMobile ? CarouselItem : 'div';

  const dateCards = (
    <>
      {isExpressDeliveryAvailable && orderStore.isExpressAvailableNow && (
        <Component className={styles.slide} key={0}>
          <ExpressDateItem
            onChange={handleSetExpressDelivery}
            isChecked={!selectedDate}
          />
        </Component>
      )}
      {/* TODO: start cards from backorder lead time */}
      {timeStamps.map((timeStamp) => {
        return (
          <Component className={styles.slide} key={timeStamp}>
            <DateItem
              timeStamp={timeStamp}
              onChange={setSelectedDate}
              isChecked={selectedDate === timeStamp}
              isDisabled={!deliverySlots[timeStamp]?.length}
            />
          </Component>
        );
      })}
    </>
  );

  const dateCardSelector = isMobile ? (
    <Carousel
      opts={carouselOpts}
      dir={userStore.dir}
      key={userStore.dir}
      className={styles.carousel}
    >
      <CarouselContent>{dateCards}</CarouselContent>
    </Carousel>
  ) : (
    dateCards
  );

  const handleSelectSlot = () => {
    if (!checkoutStore.deliveries || !currentDelivery) {
      throw new Error('Something went wrong');
    }

    if (
      currentDelivery.slotDeliveryDetails?.scheduleSlotId === selectedSlotId
    ) {
      closeModal();
      checkoutStore.slotSelectionModalStore.setSelectedSlot(null, null);
      return;
    }

    checkoutStore.slotSelectionModalStore.updateDelivery(
      selectedSlotId,
      selectedDate,
    );
    closeModal();
    checkoutStore.slotSelectionModalStore.setSelectedSlot(null, null);

    analyticsService.track(AnalyticsEvent.DELIVERY_METHOD_SELECTED, {
      type: 'Slot',
    });
  };

  const formButton = (
    <button
      data-button="deliver"
      className="button _primary _med"
      onClick={handleSelectSlot}
    >
      {t('deliverByThisTime')}
    </button>
  );

  if (!currentDelivery) {
    closeModal();
    return null;
  }

  const order = checkoutStore.slotSelectionModalStore.selectedOrder;

  if (!order) {
    throw new Error('Order not found');
  }

  const priceWithoutDelivery = +mainStore.convertPenceToPounds(
    order.paid_without_delivery,
  );

  const getSlotPrice = ({
    delivery_price,
    order_amount_threshold,
    free_delivery_threshold,
  }: DeliverySlot) => {
    const deliveryPrice = +mainStore.convertPenceToPounds(delivery_price);
    const deliveryTreshold = +mainStore.convertPenceToPounds(
      free_delivery_threshold,
    );
    const orderAmountThreshold = +mainStore.convertPenceToPounds(
      order_amount_threshold ?? 0,
    );

    return [
      orderAmountThreshold > 0
        ? t('minimumOrderValue', {
            price: formatPriceWithCurrency(orderAmountThreshold),
          })
        : null,
      deliveryPrice > 0 && deliveryTreshold > priceWithoutDelivery
        ? t('deliveryFeeValueLong', {
            price: formatPriceWithCurrency(deliveryPrice),
          })
        : t('deliveryFree'),
    ];
  };

  return (
    <Modal
      isShow={true}
      className="modal-select-date__body"
      onBackdropDismiss={() => {
        closeModal();
        checkoutStore.slotSelectionModalStore.setSelectedSlot(null, null);
      }}
    >
      <div className="popover-container input-form">
        <div className="popover">
          <button
            data-button="close"
            className="button _no-padding _no-color input-form__close"
            onClick={() => {
              closeModal();
              checkoutStore.slotSelectionModalStore.setSelectedSlot(null, null);
            }}
          >
            <Icon type="plus" className="icon__rotate-45" size={36} />
          </button>
          <div className="input-form__form">
            <div className="input-form__form-header">
              <p className="input-form__form-header-title">
                {t('scheduleDelivery')}
              </p>
              <div className="input-form__input-container">
                <div className="">
                  <ul className="input-form__input-container-days slider2">
                    {dateCardSelector}
                  </ul>
                  {!isMobile && formButton}
                </div>
                {!selectedDate ? (
                  <ul className="input-form__input-container-time">
                    <li className="list-item">
                      <input
                        data-radio="time"
                        type="radio"
                        name="time"
                        checked={true}
                        readOnly
                      />
                      <label>
                        <span className="mark" />
                        <span className="list-item__content">
                          <span className="list-item__content-primary">
                            {t('phrases:expressDeliveryInMin', {
                              // FIXME: correct eta calculation
                              time: orderStore.etaCalculation?.duration.range,
                            })}
                          </span>
                        </span>
                      </label>
                    </li>
                  </ul>
                ) : (
                  <ul className="input-form__input-container-time">
                    {checkoutStore.deliverySlots[selectedDate]?.map(
                      (
                        {
                          schedule_slot_id,
                          delivery_min_time,
                          delivery_max_time,
                          cut_off_time,
                        },
                        index,
                        slots,
                      ) => {
                        if (cut_off_time <= lastBackorder) {
                          return null;
                        }

                        const [minOrderVal, deliveryFee] = getSlotPrice(
                          slots[index]!,
                        );

                        return (
                          <li className="list-item" key={schedule_slot_id}>
                            <input
                              data-radio="time"
                              type="radio"
                              name="time"
                              value={schedule_slot_id}
                              id={schedule_slot_id}
                              checked={schedule_slot_id === selectedSlotId}
                              onChange={() =>
                                setSelectedSlotId(schedule_slot_id)
                              }
                            />
                            <label htmlFor={schedule_slot_id}>
                              <span className="mark" />
                              <span className="list-item__content">
                                <span className="list-item__content-primary">
                                  {format(
                                    new Date(delivery_min_time * 1000),
                                    timeFormat,
                                    {
                                      timeZone: company.config.timeZone,
                                      locale,
                                    },
                                  )}{' '}
                                  -{' '}
                                  {format(
                                    new Date(delivery_max_time * 1000),
                                    timeFormat,
                                    {
                                      timeZone: company.config.timeZone,
                                      locale,
                                    },
                                  )}
                                </span>
                                {minOrderVal && (
                                  <span className="list-item__content-secondary">
                                    {minOrderVal}
                                  </span>
                                )}
                                <span className="list-item__content-secondary">
                                  {deliveryFee}
                                </span>
                              </span>
                            </label>
                          </li>
                        );
                      },
                    )}
                  </ul>
                )}
              </div>
            </div>
            {isMobile && (
              <div className="input-form__form-footer">
                <div className="input-form__form-footer-buttons">
                  {formButton}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default observer(ModalSlotsSelectDate);
